"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  Close,
  Delete,
  Download,
  FilterList,
  InfoOutlined,
  Refresh,
  Search,
  Visibility,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "../../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  overflowY: "auto",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
  } transparent`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const ASSIGNMENT_LIST_API = "/assignment/list/";
const ASSIGNMENT_DETAIL_API = (id) => `/assignment/${id}/`;
const ASSIGNMENT_DOWNLOAD_API = (id) => `/assignment/download/${id}/`;
const ASSIGNMENT_DELETE_API = (id) => `/assignment/delete/${id}/`;
const SUBMISSIONS_LIST_API = "/assignment/assignment/submissions/";
const SUBMISSION_REVIEW_API = (id) => `/assignment/submission/review/${id}/`;
const COURSES_LIST_API = "/course/courses/";
const COURSE_BATCHES_API = (courseId) => `/course/courses/${courseId}/batches/`;

const api = axios.create({
  baseURL: API_BASE_URL,
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   ERROR / RESPONSE HELPERS
========================================================= */

const prettifyFieldLabel = (field) =>
  String(field || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  const data = error?.response?.data;

  if (typeof data === "string" && data.trim()) return data;

  if (data?.detail) return String(data.detail);

  if (data?.message) return String(data.message);

  if (data?.error) return String(data.error);

  if (data && typeof data === "object") {
    const entries = Object.entries(data).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    );

    const first = entries[0];

    if (first) {
      const value = Array.isArray(first[1]) ? first[1][0] : first[1];

      if (typeof value === "string") {
        return `${prettifyFieldLabel(first[0])}: ${value}`;
      }
    }
  }

  if (error?.message) return error.message;

  return fallback;
};

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.batches)) return data.batches;

  return [];
};

/* =========================================================
   COMPONENT — admin only (no student/teacher conditionals)
========================================================= */

function ViewAssignment() {
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const textPrimary = theme.palette.text.primary;

  /* Global branch selector — replaces the local
     organization/branch dropdowns. */

  const { selectedBranchId } = useBranch();

  /* =======================================================
     USER
  ======================================================= */

  const [organizationId, setOrganizationId] = useState("");

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      console.warn("Logged-in user not found.");

      return;
    }

    const orgId = user.organization_id || user.organization?.id || user.organization || "";

    setOrganizationId(orgId ? String(orgId) : "");
  }, []);

  /* =======================================================
     DATA
  ======================================================= */

  const [assignments, setAssignments] = useState([]);

  const [courses, setCourses] = useState([]);

  const [batches, setBatches] = useState([]);

  const [teachers, setTeachers] = useState([]);

  const [allBatchData, setAllBatchData] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);

  const [loadingCourses, setLoadingCourses] = useState(false);

  const [loadingBatches, setLoadingBatches] = useState(false);

  const [downloadingId, setDownloadingId] = useState(null);

  const [deletingId, setDeletingId] = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);

  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [reviewingId, setReviewingId] = useState(null);

  /* =======================================================
     SEARCH — client-side pill search
  ======================================================= */

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     FILTERS
     GET /assignment/list/ params (per Swagger):
     teacher, course, batch, organization_id, branch_id
  ======================================================= */

  const initialFilters = {
    course: "",
    batch: "",
    teacher: "",
  };

  const [filters, setFilters] = useState(initialFilters);

  const [tempFilters, setTempFilters] = useState(initialFilters);

  const [filterOpen, setFilterOpen] = useState(false);

  /* =======================================================
     PAGINATION (client-side)
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     DETAIL / SUBMISSIONS / REVIEW / DELETE
  ======================================================= */

  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);

  const [submissionsOpen, setSubmissionsOpen] = useState(false);

  const [submissionData, setSubmissionData] = useState(null);

  const [submissionTab, setSubmissionTab] = useState(0);

  const [reviewOpen, setReviewOpen] = useState(false);

  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [reviewScore, setReviewScore] = useState("");

  const [reviewComment, setReviewComment] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  const [guideOpen, setGuideOpen] = useState(false);

  /* =======================================================
     SNACKBAR — bottom right
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     FETCH COURSES — branch-scoped
  ======================================================= */

  const fetchCourses = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setCourses([]);

      return;
    }

    setLoadingCourses(true);

    try {
      const response = await api.get(COURSES_LIST_API, {
        params: {
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
        },
      });

      setCourses(getArray(response).filter((item) => item?.is_active !== false));
    } catch (error) {
      console.error("Course fetch error:", error);

      setCourses([]);

      showSnackbar(getErrorMessage(error, "Failed to load courses."), "error");
    } finally {
      setLoadingCourses(false);
    }
  }, [organizationId, selectedBranchId, showSnackbar]);

  /* =======================================================
     FETCH BATCHES — for the selected course
  ======================================================= */

  const fetchBatches = useCallback(
    async (courseId) => {
      if (!courseId) {
        setBatches([]);

        setAllBatchData([]);

        return;
      }

      setLoadingBatches(true);

      try {
        const response = await api.get(COURSE_BATCHES_API(courseId));

        const list = getArray(response).filter(
          (item) => item?.is_active !== false
        );

        setAllBatchData(list);

        setBatches(list);
      } catch (error) {
        console.error("Batch fetch error:", error);

        setBatches([]);

        setAllBatchData([]);

        showSnackbar(getErrorMessage(error, "Failed to load batches."), "error");
      } finally {
        setLoadingBatches(false);
      }
    },
    [showSnackbar]
  );

  /* =======================================================
     FETCH ASSIGNMENTS
     GET /assignment/list/
     params: organization_id, branch_id, course, batch, teacher
  ======================================================= */

  const fetchAssignments = useCallback(
    async (customFilters = filters) => {
      if (!organizationId || !selectedBranchId) {
        setAssignments([]);

        setInitialLoading(false);

        return;
      }

      setLoading(true);

      try {
        const params = {
          organization_id: Number(organizationId),
          branch_id: Number(selectedBranchId),
        };

        if (customFilters.course) params.course = Number(customFilters.course);

        if (customFilters.batch) params.batch = Number(customFilters.batch);

        if (customFilters.teacher) params.teacher = Number(customFilters.teacher);

        const response = await api.get(ASSIGNMENT_LIST_API, { params });

        setAssignments(getArray(response));
      } catch (error) {
        console.error("Assignment fetch error:", error);

        setAssignments([]);

        showSnackbar(
          getErrorMessage(error, "Failed to load assignments."),
          "error"
        );
      } finally {
        setLoading(false);

        setInitialLoading(false);
      }
    },
    [organizationId, selectedBranchId, filters, showSnackbar]
  );

  /* Re-fetch whenever the global branch or organization
     changes (also runs on mount). */

  useEffect(() => {
    if (!organizationId) return;

    fetchAssignments();

    fetchCourses();

    setFilters(initialFilters);

    setTempFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedBranchId]);

  /* Load batches whenever the temp filter course changes. */

 useEffect(() => {
  if (tempFilters.course) {
    fetchBatches(tempFilters.course);
  } else {
    setBatches([]);
    setAllBatchData([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [tempFilters.course]);

  /* Trainer — derived from the selected batch's trainer. */

  useEffect(() => {
    if (!tempFilters.batch) {
      setTeachers([]);

      return;
    }

    const selectedBatch = allBatchData.find(
      (item) => String(item.id) === String(tempFilters.batch)
    );

    if (selectedBatch && selectedBatch.trainer) {
      setTeachers([
        {
          id: selectedBatch.trainer,
          name:
            selectedBatch.trainer_name || `Trainer ${selectedBatch.trainer}`,
        },
      ]);
    } else {
      setTeachers([]);
    }
  }, [tempFilters.batch, allBatchData]);

  /* =======================================================
     SEARCH — client-side filter
  ======================================================= */

  const filteredAssignments = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return assignments;

    return assignments.filter((assignment) => {
      const title = String(assignment?.title || "").toLowerCase();

      const description = String(assignment?.description || "").toLowerCase();

      const batch = String(
        assignment?.batch_code || assignment?.batch_name || ""
      ).toLowerCase();

      const course = String(assignment?.course_name || "").toLowerCase();

      const teacher = String(assignment?.teacher_name || "").toLowerCase();

      return (
        title.includes(search) ||
        description.includes(search) ||
        batch.includes(search) ||
        course.includes(search) ||
        teacher.includes(search)
      );
    });
  }, [assignments, searchText]);

  const displayedAssignments = filteredAssignments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     ACTIVE FILTER COUNT
  ======================================================= */

  const activeFilterCount = useMemo(
    () =>
      Object.values(filters).filter((value) => value !== "" && value !== null)
        .length,
    [filters]
  );

  /* =======================================================
     FILTER HANDLERS
  ======================================================= */

 const handleTempFilterChange = (name, value) => {
  setTempFilters((prev) => {
    if (name === "course") {
      return { ...prev, course: value, batch: "", teacher: "" };
    }
    if (name === "batch") {
      return { ...prev, batch: value, teacher: "" };
    }
    return { ...prev, [name]: value };
  });
};

  const handleApplyFilters = () => {
    setFilters(tempFilters);

    setFilterOpen(false);

    setPage(0);

    fetchAssignments(tempFilters);
  };

  const handleClearFilters = () => {
    const cleared = { ...initialFilters };

    setTempFilters(cleared);

    setFilters(cleared);

    setFilterOpen(false);

    setPage(0);

    fetchAssignments(cleared);
  };

  /* =======================================================
     VIEW DETAIL
     GET /assignment/{id}/?branch_id=
  ======================================================= */

  const handleViewDetail = async (assignmentId) => {
    setDetailLoading(true);

    try {
      const response = await api.get(
        ASSIGNMENT_DETAIL_API(assignmentId),
        selectedBranchId
          ? { params: { branch_id: Number(selectedBranchId) } }
          : undefined
      );

      setSelectedAssignment(response.data);

      setDetailOpen(true);
    } catch (error) {
      console.error("Assignment detail error:", error);

      showSnackbar(
        getErrorMessage(error, "Failed to load assignment details."),
        "error"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  /* =======================================================
     DOWNLOAD PDF
     GET /assignment/download/{id}/
  ======================================================= */

  const handleDownload = async (assignment) => {
    setDownloadingId(assignment.id);

    try {
      const response = await api.get(ASSIGNMENT_DOWNLOAD_API(assignment.id), {
        responseType: "blob",
        ...(selectedBranchId
          ? { params: { branch_id: Number(selectedBranchId) } }
          : {}),
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const link = document.createElement("a");

      link.href = url;

      link.download = `${assignment.title || "assignment"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

      showSnackbar("Download started.", "success");
    } catch (error) {
      console.error("Assignment download error:", error);

      showSnackbar(
        getErrorMessage(error, "Failed to download assignment."),
        "error"
      );
    } finally {
      setDownloadingId(null);
    }
  };

  /* =======================================================
     SUBMISSIONS
     GET /assignment/assignment/submissions/?assignment=
     (falls back to the student-list summary endpoint)
  ======================================================= */

  const normalizeSubmissions = (data, assignment) => {
    if (Array.isArray(data)) {
      const submitted = data.filter(
        (item) => item?.submitted_at || item?.answer_file
      );

      return {
        assignment_title: assignment?.title || "Assignment",
        submitted_students: submitted,
        not_submitted_students: [],
        total_students: data.length,
        submitted_count: submitted.length,
        not_submitted_count: 0,
      };
    }

    const submitted = data?.submitted_students || [];

    const notSubmitted = data?.not_submitted_students || [];

    return {
      ...data,
      assignment_title:
        data?.assignment_title || assignment?.title || "Assignment",
      submitted_students: submitted,
      not_submitted_students: notSubmitted,
      total_students: data?.total_students ?? submitted.length,
      submitted_count: data?.submitted_count ?? submitted.length,
      not_submitted_count:
        data?.not_submitted_count ?? notSubmitted.length,
    };
  };

  const handleOpenSubmissions = async (assignment) => {
    setSubmissionsOpen(true);

    setSubmissionTab(0);

    setSubmissionsLoading(true);

    setSubmissionData(null);

    try {
      let response = null;

      try {
        response = await api.get(SUBMISSIONS_LIST_API, {
          params: { assignment: assignment.id },
        });
      } catch {
        response = await api.get(
          "/assignment/assignment/submission/student-list/",
          { params: { assignment: assignment.id } }
        );
      }

      setSubmissionData(normalizeSubmissions(response.data, assignment));
    } catch (error) {
      console.error("Submissions fetch error:", error);

      setSubmissionsOpen(false);

      showSnackbar(
        getErrorMessage(error, "Failed to load submissions."),
        "error"
      );
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleOpenSubmissionFile = (answerFileUrl) => {
    if (!answerFileUrl) {
      showSnackbar("No answer file attached.", "warning");

      return;
    }

    const fileUrl = String(answerFileUrl).startsWith("http")
      ? answerFileUrl
      : `${API_BASE_URL}${answerFileUrl}`;

    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  /* =======================================================
     REVIEW SUBMISSION
     POST /assignment/submission/review/{id}/
     body: { score, teacher_comment }
  ======================================================= */

  const handleOpenReview = (student) => {
    setSelectedSubmission(student);

    setReviewScore(student.score != null ? String(student.score) : "");

    setReviewComment(student.teacher_comment || "");

    setReviewOpen(true);
  };

  const handleCloseReview = () => {
    setReviewOpen(false);

    setSelectedSubmission(null);

    setReviewScore("");

    setReviewComment("");
  };

  const handleReviewSubmit = async () => {
    const submissionId =
      selectedSubmission?.submission_id || selectedSubmission?.id;

    if (!submissionId) {
      showSnackbar("Submission not found.", "error");

      return;
    }

    const score = Number(reviewScore);

    if (
      reviewScore === "" ||
      !Number.isFinite(score) ||
      score < 0 ||
      score > 100
    ) {
      showSnackbar("Please enter a score between 0 and 100.", "error");

      return;
    }

    setReviewingId(submissionId);

    try {
      const response = await api.post(
        SUBMISSION_REVIEW_API(submissionId),
        {
          score,
          teacher_comment: reviewComment?.trim() || "",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;

      if (data?.success === false) {
        showSnackbar(
          data?.message || "Failed to review submission.",
          "error"
        );

        return;
      }

      setSubmissionData((prev) => ({
        ...prev,
        submitted_students:
          prev?.submitted_students?.map((student) =>
            (student.submission_id || student.id) === submissionId
              ? {
                  ...student,
                  is_reviewed: true,
                  score: data?.score ?? score,
                  teacher_comment: data?.teacher_comment ?? reviewComment.trim(),
                  reviewed_by: data?.reviewed_by,
                  reviewed_by_name: data?.reviewed_by_name,
                  reviewed_at: data?.reviewed_at,
                }
              : student
          ) || [],
      }));

      showSnackbar("Submission reviewed successfully.", "success");

      handleCloseReview();
    } catch (error) {
      console.error("Review submission error:", error);

      showSnackbar(
        getErrorMessage(error, "Failed to review submission."),
        "error"
      );
    } finally {
      setReviewingId(null);
    }
  };

  /* =======================================================
     DELETE ASSIGNMENT (soft delete)
     DELETE /assignment/delete/{id}/?branch_id=
  ======================================================= */

  const handleDeleteConfirmed = async () => {
    const id = deleteId;

    setDeleteOpen(false);

    setDeletingId(id);

    try {
      const response = await api.delete(
        ASSIGNMENT_DELETE_API(id),
        selectedBranchId
          ? { params: { branch_id: Number(selectedBranchId) } }
          : undefined
      );

      if (response.data?.success === false) {
        showSnackbar(
          response.data?.message || "Failed to delete assignment.",
          "error"
        );

        return;
      }

      setAssignments((prev) => prev.filter((item) => item.id !== id));

      showSnackbar("Assignment deleted successfully.", "success");
    } catch (error) {
      console.error("Assignment delete error:", error);

      showSnackbar(
        getErrorMessage(error, "Failed to delete assignment."),
        "error"
      );
    } finally {
      setDeletingId(null);

      setDeleteId(null);
    }
  };

  /* =======================================================
     HELPERS
  ======================================================= */

   const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 260,
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
          },
        },
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          borderRadius: "3px",
        },
      },
    },
  };

  const selectStyles = {
    color: textPrimary,
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": { color: textPrimary },
    "& .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "&.Mui-disabled .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
      opacity: 0.6,
    },
  };

  const selectLabelStyles = {
    "& .MuiInputLabel-root": { color: textPrimary, opacity: 0.85 },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: theme.palette.background.paper,
      "& fieldset": { borderColor: theme.palette.divider },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputLabel-root": { color: textPrimary, opacity: 0.85 },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  };

  const dialogPaperProps = {
    sx: {
      backgroundColor: theme.palette.background.paper,
      color: textPrimary,
      border: `1px solid ${theme.palette.divider}`,
    },
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString() : "—";

  const headerCellStyle = {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
          {/* TOOLBAR — search, filters, refresh, guide (single row, no card) */}

  <Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flexWrap: { xs: "wrap", md: "nowrap" },
    mb: 3,
  }}
>
  <TextField
    size="small"
    placeholder="Search assignments…"
    value={searchText}
    onChange={(event) => {
      setSearchText(event.target.value);
      setPage(0);
    }}
    sx={{
      flex: 1,
      maxWidth: { xs: "100%", md: 340 },
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        height: 50,
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        "& input": {
          color: textPrimary,
          WebkitTextFillColor: textPrimary,
        },
        "& input::placeholder": {
          color: textPrimary,
          opacity: 0.75,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: isDark
            ? "rgba(255,255,255,0.28)"
            : "rgba(0,0,0,0.22)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
        },
      },
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search fontSize="small" sx={{ color: theme.palette.primary.main }} />
        </InputAdornment>
      ),
    }}
  />

  <Button
    variant="contained"
    startIcon={<FilterList />}
    onClick={() => {
      setTempFilters(filters);
      setFilterOpen(true);
    }}
    sx={{
      ml: { xs: 0, md: "auto" }, // pushes Filters + Refresh + Guide to the right on desktop
      borderRadius: "10px",
      height: 50,
      px: 3,
      boxShadow: "none",
      textTransform: "none",
      fontWeight: 600,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    Filters
    {activeFilterCount > 0 && (
      <Chip
        size="small"
        label={activeFilterCount}
        sx={{
          ml: 1,
          color: "#fff",
          backgroundColor: alpha("#fff", 0.25),
        }}
      />
    )}
  </Button>


<Tooltip title="Refresh">
  <span>
    <IconButton
      onClick={() => fetchAssignments()}
      disabled={!organizationId || !selectedBranchId || loading}
      sx={{
        width: 50,
        height: 50,
        flexShrink: 0,
        color: "#fff",
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        borderRadius: 2,

        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },

        "&.Mui-disabled": {
          color: theme.palette.action.disabled,
          backgroundColor: theme.palette.action.disabledBackground,
        },
      }}
    >
      <Refresh />
    </IconButton>
  </span>
</Tooltip>

<Tooltip title="User guide">
  <IconButton
    onClick={() => setGuideOpen(true)}
    sx={{
      width: 50,
      height: 50,
      flexShrink: 0,
      color: "#fff",
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      borderRadius: 2,

      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    }}
  >
    <InfoOutlined />
  </IconButton>
</Tooltip>

</Box>

        {/* TABLE CARD */}

        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "none",
          }}
        >
          <CustomTableScrollbar sx={{ maxHeight: 560 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellStyle}>S.No</TableCell>
                  <TableCell sx={headerCellStyle}>Trainer</TableCell>
                  <TableCell sx={headerCellStyle}>Batch</TableCell>
                  <TableCell sx={headerCellStyle}>Course</TableCell>
                  <TableCell sx={headerCellStyle}>Title</TableCell>
                  <TableCell sx={headerCellStyle}>Created At</TableCell>
                
                  <TableCell sx={headerCellStyle} align="center">
                    Download
                  </TableCell>
                  <TableCell sx={headerCellStyle} align="center">
                    Submissions
                  </TableCell>
                  <TableCell sx={headerCellStyle} align="center">
                    Delete
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {initialLoading || loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : displayedAssignments.length > 0 ? (
                  displayedAssignments.map((assignment, index) => (
                    <TableRow key={assignment.id} hover>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {assignment.teacher_name || assignment.TeacherName || "—"}
                      </TableCell>

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {assignment.batch_code ||
                          assignment.batch_name ||
                          assignment.batch ||
                          "—"}
                      </TableCell>

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {assignment.course_name || assignment.CourseName || "—"}
                      </TableCell>

                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {assignment.title || "—"}
                      </TableCell>

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {formatDate(assignment.created_at)}
                      </TableCell>

                    

                      {/* DOWNLOAD — disabled when no file */}

                      <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                        <Tooltip
                          title={
                            assignment.file
                              ? "Download PDF"
                              : "No file attached"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(assignment)}
                              disabled={
                                !assignment.file ||
                                downloadingId === assignment.id
                              }
                              sx={{
                                color: assignment.file
                                  ? theme.palette.success.main
                                  : theme.palette.action.disabled,
                                backgroundColor: assignment.file
                                  ? alpha(theme.palette.success.main, 0.08)
                                  : "transparent",
                                borderRadius: 1.5,
                              }}
                            >
                              {downloadingId === assignment.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Download fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* SUBMISSIONS — always visible for admin */}

                      <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                        <Tooltip title="View Student Submissions">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSubmissions(assignment)}
                              sx={{
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                                borderRadius: 1.5,
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      {/* DELETE */}

                      <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                        <Tooltip title="Delete Assignment">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeleteId(assignment.id);

                                setDeleteOpen(true);
                              }}
                              disabled={deletingId === assignment.id}
                              sx={{
                                color: theme.palette.error.main,
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.08
                                ),
                                borderRadius: 1.5,
                              }}
                            >
                              {deletingId === assignment.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: textPrimary }}>
                                                {selectedBranchId
                          ? "No assignments found for the selected branch."
                          : "Select a branch to load assignments."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CustomTableScrollbar>

          <Divider />

          {/* PAGINATION — inside the Card */}

          <TablePagination
            component="div"
            count={filteredAssignments.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));

              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              "& .MuiTablePagination-selectIcon": { color: textPrimary },
              "& .MuiTablePagination-actions button": { color: textPrimary },
            }}
                      />
        </Card>

        {/* FILTER DIALOG */}

              <Dialog
          open={filterOpen}
  onClose={() => setFilterOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={dialogPaperProps}
        >
          <DialogTitle
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Filter Assignments

            <IconButton onClick={() => setFilterOpen(false)} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <StyledDialogContent dividers sx={{ p: 3, display: "grid", gap: 2.5 }}>
            {/* COURSE — branch-scoped */}

            <FormControl fullWidth sx={selectLabelStyles}>
              <InputLabel>Course</InputLabel>

              <Select
                value={tempFilters.course}
                label="Course"
                onChange={(event) =>
                  handleTempFilterChange("course", event.target.value)
                }
                disabled={!selectedBranchId || loadingCourses}
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">
                  {loadingCourses ? "Loading..." : "All Courses"}
                </MenuItem>

                {courses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.course_name || `Course ${course.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* BATCH */}

            <FormControl fullWidth sx={selectLabelStyles}>
              <InputLabel>Batch</InputLabel>

              <Select
                value={tempFilters.batch}
                label="Batch"
                onChange={(event) =>
                  handleTempFilterChange("batch", event.target.value)
                }
                disabled={!tempFilters.course || loadingBatches}
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">
                  {loadingBatches ? "Loading..." : "All Batches"}
                </MenuItem>

                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.batch_name
                      ? `${batch.batch_name}${batch.batch_code ? ` (${batch.batch_code})` : ""}`
                      : batch.batch_code || `Batch ${batch.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* TRAINER — derived from the selected batch */}

            <FormControl fullWidth sx={selectLabelStyles}>
              <InputLabel>Trainer</InputLabel>

              <Select
                value={tempFilters.teacher}
                label="Trainer"
                onChange={(event) =>
                  handleTempFilterChange("teacher", event.target.value)
                }
                disabled={!tempFilters.batch}
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">All Trainers</MenuItem>

                {teachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StyledDialogContent>

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleClearFilters}
              sx={{
                borderRadius: "25px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Clear All
            </Button>

            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                borderRadius: "25px",
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* DETAIL DIALOG */}

        <Dialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Assignment Details

            <IconButton onClick={() => setDetailOpen(false)} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <StyledDialogContent dividers sx={{ p: 3 }}>
            {selectedAssignment ? (
              <>
                <Typography sx={{ mb: 1 }}>
                  <strong>Title:</strong> {selectedAssignment.title || "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Description:</strong>{" "}
                  {selectedAssignment.description || "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Trainer:</strong>{" "}
                  {selectedAssignment.teacher_name || "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Course:</strong>{" "}
                  {selectedAssignment.course_name || "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Batch:</strong>{" "}
                  {selectedAssignment.batch_name ||
                    selectedAssignment.batch_code ||
                    "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Branch:</strong>{" "}
                  {selectedAssignment.branch_name || "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Start Date:</strong>{" "}
                  {selectedAssignment.assignment_start_date || "—"}
                </Typography>

                <Typography sx={{ mb: 1 }}>
                  <strong>Submission Last Date:</strong>{" "}
                  {selectedAssignment.submission_last_date || "—"}
                </Typography>

                <Typography sx={{ mb: 3 }}>
                  <strong>Created At:</strong>{" "}
                  {selectedAssignment.created_at
                    ? new Date(
                        selectedAssignment.created_at
                      ).toLocaleString()
                    : "—"}
                </Typography>

                {selectedAssignment.file && (
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => handleDownload(selectedAssignment)}
                    sx={{ borderRadius: "25px", textTransform: "none" }}
                  >
                    Download Assignment PDF
                  </Button>
                )}
              </>
            ) : (
              <CircularProgress size={24} />
            )}
          </StyledDialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={() => setDetailOpen(false)}
              sx={{ borderRadius: "25px", boxShadow: "none", textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* SUBMISSIONS DIALOG — admin always */}

        <Dialog
          open={submissionsOpen}
          onClose={() => {
            setSubmissionsOpen(false);

            setSubmissionTab(0);
          }}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Assignment Submissions

            <IconButton
              onClick={() => {
                setSubmissionsOpen(false);

                setSubmissionTab(0);
              }}
              sx={{ color: "#fff" }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <StyledDialogContent dividers sx={{ p: 3 }}>
            {submissionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : submissionData ? (
              <>
                {/* SUMMARY */}

                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDark
                      ? alpha("#fff", 0.05)
                      : alpha("#000", 0.02),
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {submissionData.assignment_title || "Assignment"}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Typography>
                      <strong>Total Students:</strong>{" "}
                      {submissionData.total_students ?? 0}
                    </Typography>

                    <Typography>
                      <strong>Submitted:</strong>{" "}
                      {submissionData.submitted_count ?? 0}
                    </Typography>

                    <Typography>
                      <strong>Not Submitted:</strong>{" "}
                      {submissionData.not_submitted_count ?? 0}
                    </Typography>
                  </Box>
                </Box>

                {/* TABS */}

                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                  <Tabs
                    value={submissionTab}
                    onChange={(event, newValue) => setSubmissionTab(newValue)}
                    sx={{
                      "& .MuiTab-root": {
                        fontWeight: 600,
                        textTransform: "none",
                        color: textPrimary,
                        opacity: 0.8,
                      },
                      "& .MuiTab-root.Mui-selected": {
                        color: theme.palette.primary.main,
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Tab
                      label={`Submitted (${submissionData.submitted_count ?? 0})`}
                    />

                    <Tab
                      label={`Not Submitted (${
                        submissionData.not_submitted_count ?? 0
                      })`}
                    />
                  </Tabs>
                </Box>

                {/* TAB 1 — SUBMITTED */}

                {submissionTab === 0 && (
                  <CustomTableScrollbar sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellStyle}>S.No</TableCell>
                          <TableCell sx={headerCellStyle}>
                            Admission Code
                          </TableCell>
                          <TableCell sx={headerCellStyle}>Student</TableCell>
                          <TableCell sx={headerCellStyle}>Email</TableCell>
                          <TableCell sx={headerCellStyle}>
                            Submitted At
                          </TableCell>
                          <TableCell sx={headerCellStyle}>Score</TableCell>
                          <TableCell sx={headerCellStyle}>
                            Teacher Comment
                          </TableCell>
                          <TableCell sx={headerCellStyle} align="center">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {submissionData.submitted_students?.length > 0 ? (
                          submissionData.submitted_students.map(
                            (student, index) => (
                              <TableRow key={student.submission_id || index} hover>
                                <TableCell>{index + 1}</TableCell>

                                <TableCell>
                                  {student.admission_code || "—"}
                                </TableCell>

                                <TableCell>
                                  {student.candidate_name || "—"}
                                </TableCell>

                                <TableCell>{student.email || "—"}</TableCell>

                                <TableCell>
                                  {student.submitted_at
                                    ? new Date(
                                        student.submitted_at
                                      ).toLocaleString()
                                    : "—"}
                                </TableCell>

                                <TableCell>
                                  {student.score !== null &&
                                  student.score !== undefined
                                    ? `${student.score}/100`
                                    : "Not reviewed"}
                                </TableCell>

                                <TableCell
                                  sx={{
                                    maxWidth: 220,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {student.teacher_comment || "—"}
                                </TableCell>

                                <TableCell align="center">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {student.answer_file && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() =>
                                          handleOpenSubmissionFile(
                                            student.answer_file_url ||
                                              student.answer_file
                                          )
                                        }
                                        sx={{
                                          borderRadius: "25px",
                                          textTransform: "none",
                                        }}
                                      >
                                        View
                                      </Button>
                                    )}

                                    <Tooltip
                                      title={
                                        student.is_reviewed
                                          ? "Already reviewed"
                                          : "Review submission"
                                      }
                                    >
                                      <span>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => handleOpenReview(student)}
                                          disabled={student.is_reviewed === true}
                                          sx={{
                                            borderRadius: "25px",
                                            boxShadow: "none",
                                            textTransform: "none",
                                          }}
                                        >
                                          {student.is_reviewed
                                            ? "Reviewed"
                                            : "Review"}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )
                          )
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography sx={{ color: textPrimary }}>
                                No students have submitted this assignment.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CustomTableScrollbar>
                )}

                {/* TAB 2 — NOT SUBMITTED */}

                {submissionTab === 1 && (
                  <CustomTableScrollbar sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellStyle}>S.No</TableCell>
                          <TableCell sx={headerCellStyle}>
                            Admission Code
                          </TableCell>
                          <TableCell sx={headerCellStyle}>Student</TableCell>
                          <TableCell sx={headerCellStyle}>Email</TableCell>
                          <TableCell sx={headerCellStyle}>Status</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {submissionData.not_submitted_students?.length > 0 ? (
                          submissionData.not_submitted_students.map(
                            (student, index) => (
                              <TableRow
                                key={student.admission_id ?? index}
                                hover
                              >
                                <TableCell>{index + 1}</TableCell>

                                <TableCell>
                                  {student.admission_code || "—"}
                                </TableCell>

                                <TableCell>
                                  {student.candidate_name || "—"}
                                </TableCell>

                                <TableCell>{student.email || "—"}</TableCell>

                                <TableCell>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    Not Submitted
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography sx={{ color: textPrimary }}>
                                All students have submitted this assignment.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CustomTableScrollbar>
                )}
              </>
            ) : (
              <Typography sx={{ color: textPrimary }}>
                No submission data available.
              </Typography>
            )}
          </StyledDialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setSubmissionsOpen(false);

                setSubmissionTab(0);
              }}
              sx={{ borderRadius: "25px", boxShadow: "none", textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* REVIEW DIALOG */}

        <Dialog
          open={reviewOpen}
          onClose={handleCloseReview}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Review Submission
          </DialogTitle>

          <StyledDialogContent dividers sx={{ p: 3 }}>
            {selectedSubmission ? (
              <>
                <Typography>
                  <strong>Student:</strong>{" "}
                  {selectedSubmission.candidate_name || "—"}
                </Typography>

                <Typography sx={{ mt: 1, mb: 3 }}>
                  <strong>Admission Code:</strong>{" "}
                  {selectedSubmission.admission_code || "—"}
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Score (0–100)"
                  value={reviewScore}
                  onChange={(event) => setReviewScore(event.target.value)}
                  inputProps={{ min: 0, max: 100 }}
                                    sx={{ mb: 2, ...textFieldStyles }}
                />

                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={4}
                  label="Teacher Comment"
                  sx={textFieldStyles}
                  value={reviewComment}
                  onChange={(event) => setReviewComment(event.target.value)}
                />
              </>
            ) : (
              <Typography sx={{ color: textPrimary }}>
                No submission selected.
              </Typography>
            )}
          </StyledDialogContent>

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleCloseReview}
              sx={{ borderRadius: "25px", textTransform: "none", fontWeight: 600 }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={handleReviewSubmit}
              disabled={
                !selectedSubmission ||
                reviewScore === "" ||
                reviewingId !== null
              }
              sx={{
                borderRadius: "25px",
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {reviewingId !== null ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Submit Review"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DELETE CONFIRMATION DIALOG */}

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography>
              Are you sure you want to delete this assignment? This action
              cannot be undone.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => setDeleteOpen(false)}
              sx={{ borderRadius: "25px", textTransform: "none", fontWeight: 600 }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmed}
              sx={{
                borderRadius: "25px",
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* USER GUIDE DIALOG */}

        <Dialog
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            View Assignments — User Guide

            <IconButton onClick={() => setGuideOpen(false)} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <StyledDialogContent dividers sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}
            >
              ➤ Filter and view assignments
            </Typography>

            <ul style={{ marginLeft: "20px", lineHeight: 1.8 }}>
              <li>
                Pick the <strong>Branch</strong> from the global branch selector —
                the assignment list and all filter dropdowns are scoped to it.
              </li>
              <li>
                Use the search bar to search by title, description, batch,
                course, or trainer.
              </li>
              <li>
                Open <strong>Filters</strong> to narrow by Course, Batch, and
                Trainer (trainer comes from the selected batch).
              </li>
              <li>Use Refresh to reload the latest data.</li>
            </ul>

            <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mt: 3, mb: 1 }}>
              ➤ Actions available in the table
            </Typography>

            <ul style={{ marginLeft: "20px", lineHeight: 1.8 }}>
              <li>
                <strong>View</strong> — opens full assignment details.
              </li>
              <li>
                <strong>Download</strong> — downloads the attached PDF (disabled
                when there is no file).
              </li>
              <li>
                <strong>Submissions</strong> — shows submitted / not-submitted
                students; review a submission with a score (0–100) and comment.
              </li>
              <li>
                <strong>Delete</strong> — soft-deletes the assignment
                (confirmation required).
              </li>
            </ul>
          </StyledDialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              onClick={() => setGuideOpen(false)}
              sx={{ borderRadius: "25px", boxShadow: "none", textTransform: "none" }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        {/* SNACKBAR — bottom right */}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
}

export default ViewAssignment;